import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import postingSchemeService, {
  IPostingScheme,
  IPostingSchemeLine,
  PostingSchemeBase,
  PostingSchemeDirection,
  PostingSchemeSide,
  SCHEMES_QUERY_KEY,
  SCHEMES_RESOLVE_QUERY_KEY,
} from "api/postingSchemeService";
import { loadWpkTree, flattenWpk } from "data/wpk_gminy";
import { ENotificationType, notification } from "utils/notification";
import styles from "./styles.module.scss";

type EditLine = IPostingSchemeLine & { _id: string };

const DIRECTIONS: { value: PostingSchemeDirection; label: string }[] = [
  { value: "Purchase", label: "Zakup" },
  { value: "Sale", label: "Sprzedaż" },
];

const SIDES: { value: PostingSchemeSide; label: string }[] = [
  { value: "Dt", label: "Wn" },
  { value: "Ct", label: "Ma" },
];

const BASES: { value: PostingSchemeBase; label: string }[] = [
  { value: "Net", label: "Netto" },
  { value: "Vat", label: "VAT" },
  { value: "Gross", label: "Brutto" },
];

const withId = (l: IPostingSchemeLine): EditLine => ({ ...l, _id: crypto.randomUUID() });

const makeLine = (): EditLine => ({
  _id: crypto.randomUUID(),
  side: "Dt",
  account: "",
  base: "Net",
  factor: 1,
  description: "",
});

const PostingSchemeEditor = ({ contractorId }: { contractorId: string }) => {
  const { data: schemes = [], isLoading } =
    postingSchemeService.useContractorSchemes(contractorId);
  const { mutate: saveScheme, isLoading: isSaving } = postingSchemeService.useSaveScheme();
  const qc = useQueryClient();

  const [dir, setDir] = useState<PostingSchemeDirection>("Purchase");
  const [activeId, setActiveId] = useState<string | null>(null); // null = nowy schemat
  const [lines, setLines] = useState<EditLine[]>([]);
  const [name, setName] = useState("");
  const [makeDefault, setMakeDefault] = useState(true);
  const [source, setSource] = useState<string | undefined>(undefined);

  const wpkOptions = useMemo(() => flattenWpk(loadWpkTree()), []);
  const schemesForDir = useMemo(
    () => schemes.filter((s) => s.direction === dir),
    [schemes, dir],
  );

  const selectScheme = useCallback(
    (s: IPostingScheme | null, noneCount: number) => {
      setActiveId(s?.id ?? null);
      setLines((s?.lines ?? []).map(withId));
      setName(s?.name ?? "");
      setSource(s?.source);
      setMakeDefault(s ? !!s.isDefault : noneCount === 0);
    },
    [],
  );

  // Po wczytaniu/zmianie kierunku wybierz domyślny (lub pierwszy) schemat kierunku.
  useEffect(() => {
    const list = schemes.filter((s) => s.direction === dir);
    const def = list.find((s) => s.isDefault) ?? list[0] ?? null;
    selectScheme(def, list.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemes, dir]);

  const setLinesWrap = (next: EditLine[]) => setLines(next);
  const updateLine = (id: string, patch: Partial<EditLine>) =>
    setLines(lines.map((l) => (l._id === id ? { ...l, ...patch } : l)));
  const addLine = () => setLinesWrap([...lines, makeLine()]);
  const removeLine = (id: string) => setLinesWrap(lines.filter((l) => l._id !== id));

  const handleSave = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const clean: IPostingSchemeLine[] = lines.map(({ _id, ...l }) => ({
      ...l,
      account: l.account.trim(),
      factor: Number(l.factor) || 0,
    }));
    if (clean.length === 0) {
      notification({
        title: "Schemat księgowania",
        text: "Dodaj przynajmniej jeden wiersz schematu.",
        type: ENotificationType.ERROR,
      });
      return;
    }
    saveScheme(
      {
        contractorId,
        direction: dir,
        name: name.trim() || undefined,
        mode: activeId ? "update" : "add",
        schemeId: activeId ?? undefined,
        makeDefault,
        lines: clean,
      },
      {
        onSuccess: (saved) => {
          qc.invalidateQueries([SCHEMES_QUERY_KEY, contractorId]);
          qc.invalidateQueries([SCHEMES_RESOLVE_QUERY_KEY]);
          setActiveId(saved.id ?? null);
          setSource(saved.source);
          notification({
            title: "Schemat księgowania",
            text: `Zapisano schemat (${dir === "Sale" ? "sprzedaż" : "zakup"}).`,
            type: ENotificationType.INFO,
          });
        },
        onError: () =>
          notification({
            title: "Schemat księgowania",
            text: "Nie udało się zapisać schematu.",
            type: ENotificationType.ERROR,
          }),
      },
    );
  };

  const srcLabel =
    activeId === null
      ? "Nowy"
      : source === "Manual"
        ? "Ręczny"
        : source === "Auto"
          ? "Auto (z historii)"
          : "";

  return (
    <div className={styles.editor}>
      <div className={styles.tabs}>
        {DIRECTIONS.map((d) => (
          <button
            key={d.value}
            type="button"
            className={`${styles.tab} ${dir === d.value ? styles.tabActive : ""}`}
            onClick={() => setDir(d.value)}
          >
            {d.label}
          </button>
        ))}
        {srcLabel && (
          <span
            className={`${styles.sourceBadge} ${
              source === "Manual"
                ? styles.sourceManual
                : source === "Auto"
                  ? styles.sourceAuto
                  : styles.sourceNone
            }`}
          >
            {srcLabel}
          </span>
        )}
      </div>

      {/* Lista schematów kierunku (może być kilka) + „Nowy". */}
      <div className={styles.schemeChips}>
        {schemesForDir.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={`${styles.schemeChip} ${activeId === s.id ? styles.schemeChipActive : ""}`}
            onClick={() => selectScheme(s, schemesForDir.length)}
            title={s.isDefault ? "Domyślny" : undefined}
          >
            {s.isDefault ? "★ " : ""}
            {s.name || `Schemat ${i + 1}`}
          </button>
        ))}
        <button
          type="button"
          className={`${styles.schemeChip} ${styles.schemeChipNew} ${activeId === null ? styles.schemeChipActive : ""}`}
          onClick={() => selectScheme(null, schemesForDir.length)}
        >
          + Nowy
        </button>
      </div>

      {isLoading ? (
        <p className={styles.empty}>Ładowanie…</p>
      ) : (
        <>
          <div className={styles.table}>
            <div className={styles.head}>
              <div>Strona</div>
              <div>Konto</div>
              <div>Podstawa</div>
              <div>Współcz.</div>
              <div>Opis</div>
              <div />
            </div>
            {lines.length === 0 ? (
              <p className={styles.empty}>Brak wierszy — dodaj wiersz schematu.</p>
            ) : (
              lines.map((l) => (
                <div key={l._id} className={styles.row}>
                  <FormControl size="small">
                    <Select
                      value={l.side}
                      onChange={(e) =>
                        updateLine(l._id, { side: e.target.value as PostingSchemeSide })
                      }
                    >
                      {SIDES.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={wpkOptions}
                    getOptionLabel={(o) => (typeof o === "string" ? o : o.code)}
                    inputValue={l.account}
                    onInputChange={(_, val) => updateLine(l._id, { account: val })}
                    onChange={(_, val) => {
                      if (typeof val === "string") updateLine(l._id, { account: val });
                      else if (val)
                        updateLine(l._id, {
                          account: val.code,
                          description: l.description || val.label,
                        });
                    }}
                    renderOption={(props, o) => (
                      <li {...props} key={o.id} title={`${o.code} — ${o.label}`}>
                        {o.code} — {o.label}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} size="small" placeholder="Konto" />
                    )}
                  />
                  <FormControl size="small">
                    <Select
                      value={l.base}
                      onChange={(e) =>
                        updateLine(l._id, { base: e.target.value as PostingSchemeBase })
                      }
                    >
                      {BASES.map((b) => (
                        <MenuItem key={b.value} value={b.value}>
                          {b.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="number"
                    value={l.factor}
                    onChange={(e) => updateLine(l._id, { factor: Number(e.target.value) })}
                    inputProps={{ step: "0.01", min: 0, style: { textAlign: "right" } }}
                  />
                  <TextField
                    size="small"
                    value={l.description ?? ""}
                    onChange={(e) => updateLine(l._id, { description: e.target.value })}
                    placeholder="Opis"
                  />
                  <button
                    type="button"
                    className={styles.rowDel}
                    title="Usuń wiersz"
                    onClick={() => removeLine(l._id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.addBtn} onClick={addLine}>
              <FontAwesomeIcon icon={faPlus} /> Dodaj wiersz
            </button>
            <div className={styles.footerRight}>
              <TextField
                size="small"
                placeholder="Nazwa schematu (opcjonalnie)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ width: 220 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={makeDefault}
                    onChange={(e) => setMakeDefault(e.target.checked)}
                  />
                }
                label="Domyślny"
                sx={{ mr: 0 }}
              />
              <button
                type="button"
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={isSaving}
              >
                {activeId ? "Zapisz zmiany" : "Dodaj schemat"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PostingSchemeEditor;
